import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AdminActions } from "src/redux/Admin/actions";
import { getTermsState } from "src/utils/auth";
import { formatKst } from "src/utils/dates";
import { ModalActions } from "src/redux/Modal/actions";
import PreviousTerms from "./PreviousTerms";
import styles from "./styles.module.scss";

// A short, readable device from the user-agent (browser + OS); full text on hover.
const deviceLabel = (ua) => {
    if (!ua) return "-";
    const browser = /Edg\//.test(ua) ? "Edge"
        : /Chrome\//.test(ua) ? "Chrome"
        : /Firefox\//.test(ua) ? "Firefox"
        : /Safari\//.test(ua) ? "Safari"
        : "Browser";
    const os = /Windows/.test(ua) ? "Windows"
        : /Mac OS X|Macintosh/.test(ua) ? "macOS"
        : /Android/.test(ua) ? "Android"
        : /iPhone|iPad/.test(ua) ? "iOS"
        : /Linux/.test(ua) ? "Linux"
        : "";
    return os ? `${browser} / ${os}` : browser;
};

// How long a session has been active (now - created), coarse-grained.
/**
 * What this user's consent amounts to, against the version in force.
 *
 * Three answers, and the middle one is worth keeping distinct: somebody on an
 * older version agreed to the previous terms and has not signed in since the
 * new ones shipped, which is not the same as never having agreed at all.
 */
const acceptedOn = (entry) => formatKst(entry.acceptedTimestamp) || null;

const termsStatus = (user, currentVersion) => {
    const history = user.acceptedTerms || [];
    if (history.length === 0) {
        return { label: "Never accepted", stale: true, history };
    }
    const current =
        currentVersion && history.find((e) => e.version === currentVersion);
    if (current) {
        return {
            label: `Accepted on: ${acceptedOn(current) || "unknown"}`,
            stale: false,
            history,
        };
    }
    // Newest first from the query, so the head is the most recent they agreed to.
    const latest = history[0];
    return {
        label: `Not accepted. On ${latest.version}, agreed ${acceptedOn(latest) || "at an unknown time"}`,
        stale: true,
        history,
    };
};

const activeFor = (createdAtMs) => {
    if (!createdAtMs) return "-";
    let s = Math.max(0, Math.floor((Date.now() - Number(createdAtMs)) / 1000));
    const d = Math.floor(s / 86400); s -= d * 86400;
    const h = Math.floor(s / 3600); s -= h * 3600;
    const m = Math.floor(s / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return "just now";
};

// The inline panel shown when a user row is expanded: account facts plus the
// user's active BFF sessions (device, IP, how long active), each revocable.
const UserDetail = ({ user }) => {
    const dispatch = useDispatch();
    const realm = user.realm || (user.cpty ? "truweb" : "ksl-admin");

    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        dispatch(AdminActions.FetchUserSessions(realm, user.id))
            .then((rows) => setSessions(rows || []))
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [dispatch, realm, user.id]);

    useEffect(() => { load(); }, [load]);

    const revoke = async (sessionId) => {
        try {
            await dispatch(AdminActions.RevokeUserSession(realm, user.id, sessionId));
            load();
        } catch (e) {
            setError(e.message);
        }
    };

    const revokeAll = async () => {
        try {
            await dispatch(AdminActions.RevokeAllUserSessions(realm, user.id));
            load();
        } catch (e) {
            setError(e.message);
        }
    };

    // Counterparty users consent; operators have no counterparty and no terms.
    const currentTermsVersion = getTermsState().version;
    const terms = user.cpty ? termsStatus(user, currentTermsVersion) : null;
    // Everything except the version they hold now, which the line above states.
    const previous = (terms?.history || []).filter(
        (e) => e.version !== currentTermsVersion
    );

    return (
        <div className={styles.detailPanel}>
            {terms && (
                <div className={styles.detailSection}>
                    <div className={styles.detailSectionTitle}>
                        Terms &amp; Privacy
                    </div>
                    <div
                        className={terms.stale ? styles.unverified : styles.enabled}
                        data-testid={`user-terms-${user.email}`}
                    >
                        {terms.label}
                    </div>
                    {previous.length > 0 && (
                        <div className={styles.detailPrevious}>
                            <button
                                className={styles.actionLink}
                                data-testid={`user-previous-terms-${user.email}`}
                                onClick={() =>
                                    dispatch(
                                        ModalActions.Open(PreviousTerms, {
                                            width: 460,
                                            customProps: {
                                                email: user.email,
                                                entries: previous.map((e) => ({
                                                    version: e.version,
                                                    accepted: acceptedOn(e) || "unknown",
                                                })),
                                            },
                                        })
                                    )
                                }
                            >
                                Previously accepted ({previous.length})
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className={styles.detailSection}>
                <div className={styles.detailSectionHead}>
                    <div className={styles.detailSectionTitle}>Active sessions</div>
                    {sessions.length > 0 && (
                        <button
                            className={styles.sessionAction}
                            data-testid={`sessions-revoke-all-${user.email}`}
                            onClick={revokeAll}
                        >
                            Sign out all
                        </button>
                    )}
                </div>

                {error && <div className={styles.error}>{error}</div>}

                {loading ? (
                    <div className={styles.detailMuted}>Loading sessions…</div>
                ) : sessions.length === 0 ? (
                    <div className={styles.detailMuted} data-testid={`user-sessions-${user.email}`}>
                        No active sessions.
                    </div>
                ) : (
                    <table
                        className={`${styles.table} ${styles.historyTable}`}
                        data-testid={`user-sessions-${user.email}`}
                    >
                        <thead>
                            <tr><th>Active for</th><th>Device</th><th>IP</th><th>Actions</th></tr>
                        </thead>
                        <tbody>
                            {sessions.map((s) => (
                                <tr key={s.id}>
                                    <td>{activeFor(s.createdAtMs)}</td>
                                    <td title={s.userAgent || ""}>{deviceLabel(s.userAgent)}</td>
                                    <td>{s.ip || "-"}</td>
                                    <td>
                                        <button
                                            className={styles.sessionAction}
                                            data-testid={`session-revoke-${s.id}`}
                                            onClick={() => revoke(s.id)}
                                        >
                                            Sign out
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default UserDetail;
